import { useState } from 'react';
import { toast } from 'sonner';
import { StickyNote } from 'lucide-react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const fmtWhen = (iso) => iso
  ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '';

/**
 * A small, self-contained note bubble for a part. Amber/filled when a note exists, a muted outline
 * when empty. Clicking opens the editor. Keeps its own copy of the note so it reflects saves
 * immediately; `onSaved(note, by, at)` lets a parent list sync its own state too.
 */
export function PartNoteButton({ part, onSaved, size = 13, attention = false, revealOnHover = false }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(part.note || '');
  const [meta, setMeta] = useState({ by: part.note_updated_by, at: part.note_updated_at });
  const hasNote = !!(note && note.trim());

  const handleSaved = (n, by, at) => {
    setNote(n || '');
    setMeta({ by, at });
    onSaved?.(n, by, at);
  };

  // A note that exists reads strongest; a negative part with no note yet gets an attention-grabbing
  // amber call-to-action; anything else is a quiet outline.
  const quiet = !hasNote && !attention;
  const cls = hasNote
    ? 'text-amber-700 bg-amber-100 hover:bg-amber-200 border border-amber-300'
    : attention
      ? 'text-amber-600 bg-amber-50 hover:bg-amber-100 border border-amber-300'
      : 'text-slate-300 hover:text-slate-600 hover:bg-slate-100 border border-transparent';
  // On a normal part inside a table row, stay hidden until the row is hovered (keeps the column
  // clean) — but always show it once there's a note or the part needs attention.
  const visibility = (revealOnHover && quiet) ? 'opacity-0 group-hover:opacity-100 focus:opacity-100' : '';

  return (
    <>
      <button onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`part-note-${part.part_id}`}
        title={hasNote ? `Note: ${note}` : (attention ? 'Add a note — this part needs attention' : 'Add a note')}
        className={`p-1 rounded-sm transition relative shrink-0 ${cls} ${visibility}`}>
        <StickyNote size={size} className={hasNote ? 'fill-amber-300' : ''} />
        {hasNote && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-amber-500 rounded-full" />}
      </button>
      {open && (
        <PartNoteModal
          part={{ ...part, note, note_updated_by: meta.by, note_updated_at: meta.at }}
          onClose={() => setOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}

function PartNoteModal({ part, onClose, onSaved }) {
  const [text, setText] = useState(part.note || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/parts/${part.part_id}/note`, { note: text });
      const { note, note_updated_by, note_updated_at } = res.data;
      onSaved?.(note, note_updated_by, note_updated_at);
      toast.success(note ? 'Note saved' : 'Note cleared');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-900 flex items-center gap-2">
            <StickyNote size={16} className="text-amber-500" />
            Note — <span className="font-mono text-orange-500 text-sm">{part.part_number}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 mt-1">
          <p className="text-xs text-slate-500">{part.part_name}{part.customer_name ? ` · ${part.customer_name}` : ''}</p>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} autoFocus
            data-testid="part-note-input" placeholder="Reason, action or follow-up for this part…"
            className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 resize-y" />
          {part.note_updated_by && (
            <p className="text-[10px] text-slate-400">Last updated by {part.note_updated_by}{part.note_updated_at ? ` · ${fmtWhen(part.note_updated_at)}` : ''}</p>
          )}
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 h-9 border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-part-note-btn"
              className="flex-1 h-9 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Note'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
